// The single, shared definition of a bug report's structured `analysis` (stored on bugs.analysis,
// a JSONB column). Both the backend (generation, evidence resolution, webhook/markdown rendering)
// and the frontend (Redux slice, BugReport component) import these types — do NOT redefine them
// elsewhere. One tsconfig covers both `src` and `netlify`, so this file imports cleanly on either
// side (backend with a `.ts` extension, frontend without).

export interface EvidenceItem {
  tool: string
  params: Record<string, unknown>
  // The recording each evidence item was captured against. A bug can span multiple recordings
  // (e.g. browser + driver), so evidence carries its own recording id; the server re-executes the
  // tool call against it, and the UI links back to the right recording.
  recording_id?: string
  result?: string | null
}

export interface AnalysisSection {
  text: string
  evidence?: EvidenceItem[]
}

// Structured description of the interaction on an `interaction` chronology step. Filled MECHANICALLY
// from the step's evidence (UserInteractions / Screenshot / InspectElement) by bug-management/file-script — it
// is NOT hand-authored — so the frontend and judge can read it directly instead of re-parsing opaque
// MCP text. `evidence` on the step remains the raw, re-executable source this is distilled from.
export interface InteractionInfo {
  // The input modality. 'mouse' for pointer actions, 'keyboard' for key input.
  type: 'mouse' | 'keyboard'
  // The specific action, e.g. 'click', 'dblclick', 'type', 'keypress'. Free-form so new action
  // kinds don't require a schema change.
  action?: string
  // Where the interaction happened, as normalized [0..1] fractions of the frame (like highlight_box),
  // so the red-circle overlay tracks the image at any rendered size. Present for mouse actions and for
  // keyboard actions targeting a focused element (the element's center); null when there is no point
  // (e.g. a global keypress).
  location?: { x: number; y: number } | null
  // For keyboard interactions: the key(s) pressed or text typed, e.g. 'Enter', 'Ctrl+A', 'hello'.
  keys?: string
  // The element acted on, when identifiable from the evidence.
  target?: { selector?: string; label?: string }
}

export interface ChronologyStep {
  text?: string
  // Whether this step is a user action or a resulting observation. Optional for legacy steps; new
  // steps set it, and it discriminates whether interaction_info is expected.
  kind?: 'interaction' | 'observation'
  // Millisecond offset into the recording that this step corresponds to. Screenshot URLs encode the
  // same offset in their filename (screenshot-<ms>), so a step's screenshot can be checked against it.
  timestamp?: number
  // LEGACY, no longer written. The displayed frame is DERIVED from the step's Screenshot evidence —
  // see stepScreenshotUrl. Still read as a fallback for bugs filed before that change.
  screenshot_url?: string | null
  // Present on interaction steps (kind === 'interaction'); null/absent on observations. Filled
  // mechanically from evidence — see InteractionInfo.
  interaction_info?: InteractionInfo | null
  // Normalized [x, y, w, h] (fractions of the frame, 0..1) marking the affected region in this
  // step's screenshot. Set by the polish bug filer (bug-management/file-script) from its confirmed on-screen
  // frame. The judge is told the region in words; the UI overlays a rectangle on the image.
  // Preserved through resolveEvidenceItems (which only fills in evidence results) and stored on
  // bugs.analysis.
  highlight_box?: number[] | null
  evidence?: EvidenceItem[]
}

export interface BugAnalysis {
  impact?: string | null
  chronology?: ChronologyStep[]
  root_cause: AnalysisSection
  chain: AnalysisSection[]
}

// ── The frame a chronology step displays ──────────────────────────────────────────────────────────

// Parse the rendered-frame URL out of a Screenshot MCP result. The tool snaps a requested timestamp to
// the most recent captured frame at/before it and reports THAT frame's URL — which is guaranteed to
// exist, unlike one built from the requested ms (only captured offsets have a persisted frame, so a
// URL keyed to an interaction time 404s).
//
// The filename tail is NOT just the timestamp: an element-scoped Screenshot (one taken with `element`,
// which is how a below-the-fold region gets a frame that actually shows it) is named
// `screenshot-Element:7-1226.jpg`. A `\d+`-only tail read that as "no frame", so the step rendered no
// image and the structural gate rejected the bug for having none.
export function frameUrlFromScreenshotResult(result: string | null | undefined): string | null {
  const m = String(result ?? '').match(/https:\/\/static\.replay\.io\/recordings\/[^\s"']+\/analysis\/screenshot-[^\s"'/]+\.jpg/)
  return m ? m[0] : null
}

// The frame to display for a chronology step. DERIVED from the step's Screenshot evidence rather than
// stored beside it: a stored URL and the evidence it was supposed to come from can drift apart (they
// did — a URL built from the requested ms while the evidence held the snapped frame), and the derived
// one is by construction the frame the evidence actually returned.
//
// Falls back to the legacy screenshot_url for bugs filed before the field stopped being written. Takes
// a structural type so both ChronologyStep definitions (this one and guidance's) satisfy it.
export function stepScreenshotUrl(step: {
  screenshot_url?: string | null
  evidence?: { tool?: string; result?: string | null }[]
} | null | undefined): string | null {
  for (const e of step?.evidence ?? []) {
    if (e?.tool !== 'Screenshot') continue
    const url = frameUrlFromScreenshotResult(e.result)
    if (url) return url
  }
  return step?.screenshot_url || null
}

// The recording offset the frame at `url` was captured at — the TRAILING number of the filename, since an
// element-scoped frame carries a prefix before it (`screenshot-Element:7-1226.jpg` is 1226ms). Null when the
// URL names none, which is not the same as 0.
//
// This is what makes a step's frame CHECKABLE against the moment the step is about: the two are independent
// (`timestamp` is the moment, the frame is whatever Screenshot snapped for it), and a reader comparing them
// otherwise has to re-parse the filename itself.
export function frameMsFromUrl(url: string | null | undefined): number | null {
  const m = String(url ?? '').match(/-(\d+)\.jpg$/)
  if (!m) return null
  const ms = Number(m[1])
  return Number.isFinite(ms) ? ms : null
}
